import { useState, ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';

import theme from '@styles/theme';
import { useFileUpload } from '@hooks/useFileUpload';
import { isUrl, onlyNumber } from '@utils/common';
import { SEE_PRODUCT_LIST } from '@graphql/queries';
import { CREATE_PRODUCT } from '@graphql/mutations';
import { Mutation } from '@graphql/types';

import { FlexBox } from '@components/styled/StyledComponents';
import StyledModal from '@components/styled/StyledModal';
import StyledInput from '@components/styled/StyledInput';
import StyledButton from '@components/styled/StyledButton';
import StyledFileUpload from '@components/styled/StyledFileUpload';

type AddProductModalProps = {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
};

const AddProductModal = ({ isOpen, setIsOpen }: AddProductModalProps) => {
    const { file, previewUrl, onChangeFile, onDeleteFile } = useFileUpload();
    const [loading, setLoading] = useState(false);
    const [inputs, setInputs] = useState({
        productName: '',
        url: '',
        price: ''
    });

    const [createProduct] =
        useMutation<Pick<Mutation, 'createProduct'>>(CREATE_PRODUCT);

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'price') {
            return setInputs({
                ...inputs,
                [name]: onlyNumber(value)
            });
        }

        setInputs((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const onSubmit = async () => {
        const { productName, url, price } = inputs;

        if (!productName) {
            return toast.warning('상품명을 입력해주세요.');
        } else if (!url) {
            return toast.warning('스토어 링크를 입력해주세요.');
        } else if (!file) {
            return toast.warning('상품 사진을 등록해주세요.');
        }

        setLoading(true);

        try {
            await createProduct({
                variables: {
                    name: productName,
                    url,
                    price: +price,
                    img: file
                },
                onCompleted: () => {
                    toast.success('상품을 등록했습니다.');
                    setIsOpen(false);
                },
                refetchQueries: [SEE_PRODUCT_LIST]
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <StyledModal
            title="상품 등록"
            isOpen={isOpen}
            closeBtnHandler={() => setIsOpen(false)}
            marginBottom={32}
            width="468px"
        >
            <StyledInput
                name="productName"
                title="상품명"
                value={inputs.productName}
                onChange={onChange}
                placeholder="상품명을 입력해주세요."
                margin="0 0 8px"
                COLUMN
            />
            <StyledInput
                name="price"
                title="상품 가격"
                value={inputs.price}
                onChange={onChange}
                placeholder="상품 가격을 입력해주세요."
                margin="0 0 8px"
                COLUMN
            />
            <StyledInput
                name="url"
                title="스토어 링크"
                value={inputs.url}
                onChange={onChange}
                placeholder="스토어 링크를 입력해주세요."
                margin="0 0 8px"
                COLUMN
            />
            <StyledFileUpload
                title="상품 사진"
                onChange={onChangeFile}
                onDelete={onDeleteFile}
                previewUrl={previewUrl}
                file={file}
            />
            <FlexBox $gap={8} $justifyContent="flex-end" $margin="32px 0 0">
                <StyledButton
                    title="취소"
                    width={140}
                    bgColor={theme.colors.whiteColor}
                    fontColor={theme.colors.blackColor}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    onClick={() => setIsOpen(false)}
                />
                <StyledButton
                    title="등록"
                    width={140}
                    bgColor={theme.colors.pointGreen}
                    onClick={onSubmit}
                    loading={loading}
                />
            </FlexBox>
        </StyledModal>
    );
};

export default AddProductModal;
